import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useSearchParams } from "react-router-dom";
import { notify } from "utils/notify";
import {
  AuthContainer,
  AuthSidebar,
  LogoContainer,
  LogoSeparator,
  BrandLogo,
  AnniversaryLogo,
  AuthTitle,
  AuthContent,
  PoolProLogo,
  SidebarFormContainer,
  SidebarFormTitle,
  AuthDescription,
  AuthMessageBlock,
  AuthSuccessIcon,
  FormGroup,
  Label,
  PasswordField,
  PasswordInput,
  PasswordToggle,
  ErrorMessage,
  Button,
} from "styles/auth.styles";
import daveyLogo from "assets/Davey_logotype_Yellow.svg";
import anniversaryLogo from "assets/90_years_logo_White.svg";
import poolProfessionalLogo from "assets/PoolProfessional_STACK_Yellow_White.png";
import { resetAppUserPassword } from "services/authService";

type FormData = {
  newPassword: string;
  confirmPassword: string;
};

const schema = yup.object().shape({
  newPassword: yup
    .string()
    .required("Password is required")
    .min(8, "Password must be at least 8 characters"),
  confirmPassword: yup
    .string()
    .oneOf([yup.ref("newPassword")], "Passwords must match")
    .required("Please confirm your password"),
});

const UserResetPassword: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [resetCompleted, setResetCompleted] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const pin = searchParams.get("token") || "";
  const hasPin = !!pin;

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    if (!hasPin) notify.error("Invalid reset link. Missing token.");
  }, [hasPin]);

  const onSubmit = async (data: FormData) => {
    if (!hasPin) return;
    setIsSubmitting(true);
    try {
      await resetAppUserPassword({ token: pin, newPassword: data.newPassword });
      setResetCompleted(true);
      notify.success("Your password has been reset successfully!");
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        "Failed to reset password. The PIN may have expired. Please contact support.";
      notify.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderContent = () => {
    if (resetCompleted) {
      return (
        <AuthMessageBlock>
          <AuthSuccessIcon aria-hidden>✓</AuthSuccessIcon>
          <SidebarFormTitle>Password Updated!</SidebarFormTitle>
        </AuthMessageBlock>
      );
    }

    if (!hasPin) {
      return (
        <AuthMessageBlock>
          <SidebarFormTitle>Invalid Link</SidebarFormTitle>
          <AuthDescription>
            This reset link is invalid or has expired. Please contact your
            administrator to send a new password reset email.
          </AuthDescription>
        </AuthMessageBlock>
      );
    }

    return (
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <SidebarFormTitle>Set New Password</SidebarFormTitle>
        <AuthDescription>
          Enter a new password for your account.
        </AuthDescription>

        <FormGroup>
          <Label htmlFor="newPassword">New Password</Label>
          <PasswordField>
            <PasswordInput
              id="newPassword"
              type={showPassword ? "text" : "password"}
              placeholder="Min. 8 characters"
              autoComplete="new-password"
              {...register("newPassword")}
              disabled={isSubmitting}
            />
            <PasswordToggle
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              aria-label={showPassword ? "Hide password" : "Show password"}
              disabled={isSubmitting}
            >
              <i className={`pi ${showPassword ? "pi-eye-slash" : "pi-eye"}`} />
            </PasswordToggle>
          </PasswordField>
          {errors.newPassword && (
            <ErrorMessage>{errors.newPassword.message}</ErrorMessage>
          )}
        </FormGroup>

        <FormGroup>
          <Label htmlFor="confirmPassword">Confirm Password</Label>
          <PasswordField>
            <PasswordInput
              id="confirmPassword"
              type={showConfirm ? "text" : "password"}
              placeholder="Repeat your password"
              autoComplete="new-password"
              {...register("confirmPassword")}
              disabled={isSubmitting}
            />
            <PasswordToggle
              type="button"
              onClick={() => setShowConfirm((v) => !v)}
              aria-label={
                showConfirm ? "Hide confirm password" : "Show confirm password"
              }
              disabled={isSubmitting}
            >
              <i className={`pi ${showConfirm ? "pi-eye-slash" : "pi-eye"}`} />
            </PasswordToggle>
          </PasswordField>
          {errors.confirmPassword && (
            <ErrorMessage>{errors.confirmPassword.message}</ErrorMessage>
          )}
        </FormGroup>

        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? "Updating..." : "Update Password"}
        </Button>
      </form>
    );
  };

  return (
    <AuthContainer>
      <AuthSidebar>
        <LogoContainer>
          <BrandLogo src={daveyLogo} alt="Davey Logo" />
          <LogoSeparator />
          <AnniversaryLogo src={anniversaryLogo} alt="90 Years Logo" />
        </LogoContainer>
        <AuthTitle>PoolProfessional</AuthTitle>
        <SidebarFormContainer>{renderContent()}</SidebarFormContainer>
      </AuthSidebar>
      <AuthContent>
        <PoolProLogo
          src={poolProfessionalLogo}
          alt="Davey Pool Professional Logo"
        />
      </AuthContent>
    </AuthContainer>
  );
};

export default UserResetPassword;
